<template>
  <div class="display-field-wrapper">
    <div class="display-field-container">
      <div class="display-field-value custom-input">
        {{ displayValue }}
      </div>
      <label v-if="label" class="display-field-label input-label input-label--floating">
        {{ label }}
      </label>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

interface Props {
  label?: string;
  value?: unknown;
  joinStringArray?: boolean;
  stringArraySeparator?: string;
  backgroundColor?: string;
  borderRadius?: string;
  textColor?: string;
  placeholderColor?: string;
  minimumHeight?: string;
}

const props = withDefaults(defineProps<Props>(), {
  label: undefined,
  value: undefined,
  joinStringArray: false,
  stringArraySeparator: ', ',
  backgroundColor: 'var(--ion-input-background)',
  borderRadius: 'var(--ion-radius-lg)',
  textColor: 'var(--ion-text-color)',
  placeholderColor: 'var(--ion-input-placeholder-color)',
  minimumHeight: '48px',
});

const displayValue = computed(() => {
  if (props.value === null || props.value === undefined) {
    return '—';
  }
  if (
    props.joinStringArray &&
    Array.isArray(props.value) &&
    props.value.every((item) => typeof item === 'string')
  ) {
    return props.value.join(props.stringArraySeparator);
  }
  if (typeof props.value === 'string' || typeof props.value === 'number') {
    return String(props.value);
  }
  if (typeof props.value === 'boolean') {
    return props.value ? 'true' : 'false';
  }
  return JSON.stringify(props.value);
});
</script>

<style scoped>
.display-field-wrapper {
  position: relative;
  width: 100%;
}

.display-field-container {
  position: relative;
}

.display-field-value {
  width: 100%;
  background-color: v-bind('props.backgroundColor');
  border-radius: v-bind('props.borderRadius');
  box-sizing: border-box;
  font-family: inherit;
  font-size: 14px;
  border: 1px solid var(--ion-input-border-color);
  padding: 12px 16px;
  min-height: v-bind('props.minimumHeight');
  transition: border-color 0.2s ease-in-out;
  outline: none;
  color: v-bind('props.textColor');
  cursor: default;
  user-select: text;
  word-break: break-word;
  white-space: pre-wrap;
  overflow-wrap: break-word;
}

.display-field-label {
  position: absolute;
  left: 16px;
  top: 0;
  transform: translateY(-50%);
  font-size: 12px;
  color: var(--ion-input-border-color-focus, #3b82f6);
  pointer-events: none;
  transition: all 0.2s ease-in-out;
  background-color: v-bind('props.backgroundColor');
  padding: 0 4px;
  z-index: 1;
}

.display-field-container:has(.display-field-value) .display-field-value {
  padding-top: 12px;
}
</style>
